/**
 * Wire schema for `GET /api/v4/action-definitions` (`src/api/v4/action-routes.ts`).
 * `ActionDefinitionSchema` is the full row the route sends. The contact card's
 * Actions menu (`ContactActionMenus.tsx`) reads only four of its fields, so
 * `InstantActionDefinitionSchema` is a `.pick()` of it: the browser's parse of
 * the contact menu ignores `inputSchema` and the rest of the row. The route
 * itself still validates `inputSchema` before sending, and a definition whose
 * stored `input_schema` has drifted from the field shape is dropped from the
 * list (logged), so it does not appear in the menu at all.
 */

import { z } from 'zod';
import { DATA_TYPES } from '../view-system/registries/data-types';

/**
 * The flat, closed set of contact identity keys an instant action's
 * `required_contact_fields` can name — and the ONLY keys
 * `ContactActionMenus.tsx` gates a menu item's visibility on.
 *
 * These keys must stay a SUBSET of `resolveContactSourceField`'s closed
 * switch map (`src/services/action-execution-service.ts`) — that map is what
 * actually resolves a `sourceField` server-side at execute time, so a key
 * here that map doesn't handle would gate on a field that can never resolve
 * (and, symmetrically, a required map entry missing here would silently
 * un-gate a field the server can't fill in).
 *
 * `'name'` is deliberately EXCLUDED even though the resolution map has a case
 * for it: `hydrateContactName` resolves a missing name SERVER-SIDE from
 * `SalesforceContact` for any schema that binds `sourceField: 'name'` — the
 * execute wire (`contact_id`/`contact_email`/`account_id`) never carries a
 * name at all. Gating on a frontend `contactName` prop would over-hide
 * actions (e.g. `route_to_gtme`, whose `Full Name` field is `required` and
 * sourced from `'name'`) even in the common case where the server would
 * successfully hydrate one.
 *
 * Play-style dotted sourceFields (`'contact.email'`, `'input.subject'`, …)
 * are deliberately unmatched here on purpose, not by omission — plays and
 * instant actions use different sourceField vocabularies resolved by
 * different pipelines (plays resolve upstream in enroll-service; instant
 * actions resolve via the flat closed map above). See
 * `src/services/flow/play-capabilities.ts`'s header for the play-side
 * derivation this deliberately does NOT unify with.
 */
export const GATEABLE_CONTACT_SOURCE_FIELDS = {
  id: 'Contact ID',
  email: 'Email',
  account_id: 'Account ID',
} as const;
export type GateableContactSourceField = keyof typeof GATEABLE_CONTACT_SOURCE_FIELDS;

export const RequiredContactFieldSchema = z.object({
  source_field: z.string(),
  label: z.string(),
});
export type RequiredContactField = z.infer<typeof RequiredContactFieldSchema>;

/** One field of an action definition's `inputSchema`; `contextKey` pre-fills it from the URL. */
export const ActionDefinitionInputFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  type: z.enum(DATA_TYPES),
  required: z.boolean().optional(),
  options: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
  sourceField: z.string().optional(),
  staticValue: z.string().optional(),
  optionsSource: z.enum(['warm_intro_connectors', 'warm_intro_preferred_channel']).optional(),
  placeholder: z.string().optional(),
  contextKey: z.string().optional(),
});
export type ActionDefinitionInputField = z.infer<typeof ActionDefinitionInputFieldSchema>;
export const ActionDefinitionInputFieldListSchema = z.array(ActionDefinitionInputFieldSchema);

export const ActionDefinitionSchema = z.object({
  id: z.number(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  inputSchema: ActionDefinitionInputFieldListSchema,
  expectedArtifactCount: z.number(),
  blueprintKind: z.string().nullable(),
  handler: z.string(),
  webhookDestination: z.string().optional(),
  // `.default([])`: a definition missing the key degrades to "nothing required"
  // (still shown), not a failed parse that would hide the whole Actions menu.
  required_contact_fields: z.array(RequiredContactFieldSchema).default([]),
});
export type ActionDefinition = z.infer<typeof ActionDefinitionSchema>;
export const ActionDefinitionsResponseSchema = z.array(ActionDefinitionSchema);

export const InstantActionDefinitionSchema = ActionDefinitionSchema.pick({
  slug: true,
  name: true,
  description: true,
  required_contact_fields: true,
});
export type InstantActionDefinition = z.infer<typeof InstantActionDefinitionSchema>;

export const InstantActionDefinitionsResponseSchema = z.array(InstantActionDefinitionSchema);
